import React from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { VALID_GAME_ID } from '@root/constants';
import { useAutoRollCountdown } from '@root/hooks';
import { appSelector, AppSliceState } from '@root/store/slices/appSlice';
import { roundSelector } from '@root/store/slices/roundSlice';
import {
  useActions,
  useGetGameRulesV2Query,
  useTypedSelector,
} from '@root/store/store';
import { RulesViewer } from '@shared/components';
import { useImageUrl } from '@shared/components/ImageContext';
import { useAnalytics } from '@shared/hooks';
import { AnalyticsEvent } from '@shared/services/analytics';
import { findRuleLayoutForLanguage } from '@shared/store/types';
import { useTranslation } from 'react-i18next';
import styles from './GameRulesModal.module.scss';

// Fallback copy. Anything the rules service returns wins.
const STEPS: [string, string][] = [
  [
    'Choose colour and roll amount',
    'Each paid roll costs the selected amount. It stays fixed for this round.',
  ],
  [
    'Roll before the countdown ends',
    'After 10 seconds, the game rolls automatically—even while you are away.',
  ],
  [
    'Collect rewards on landing',
    '2X and 3X tiles pay immediately. A free-roll tile makes your next roll free. Passing a tile does nothing.',
  ],
  [
    'Avoid the skulls',
    'Landing on a skull ends the round. You keep multiplier rewards already paid.',
  ],
  [
    'Finish with the exact roll',
    'Reach 12 exactly to win 6X. Roll too high and stay in place; that roll is still used.',
  ],
  [
    'Out of balance?',
    'Abandon for a loss, or add balance and resume within 60 minutes. Otherwise the round is abandoned automatically.',
  ],
];

const GameRulesModal: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { gameRulesModalOpen } = useTypedSelector<AppSliceState>(appSelector);
  const { round } = useTypedSelector(roundSelector);
  const { setGameRulesModalOpen } = useActions();
  const { trackEvent, trackPage } = useAnalytics();
  const { img, imgProps } = useImageUrl();
  const seconds = useAutoRollCountdown(
    round?.state?.rollDeadline,
    gameRulesModalOpen
  );

  const { data: rulesData } = useGetGameRulesV2Query(
    gameRulesModalOpen ? VALID_GAME_ID : skipToken
  );

  const layout =
    findRuleLayoutForLanguage(rulesData?.data?.rules ?? [], i18n.language) ??
    null;

  if (!gameRulesModalOpen) return null;

  const close = () => {
    trackPage(AnalyticsEvent.RULES_CLOSED);
    trackEvent(AnalyticsEvent.INSTANT_GAME_RULES_CANCEL);
    setGameRulesModalOpen(false);
  };

  return (
    <div className={styles.scrim}>
      <div
        className={styles.dialog}
        style={
          { '--panel-skin': `url(${img('PopupFrame')})` } as React.CSSProperties
        }
      >
        <div className={styles.head}>
          <h2 className={styles.title}>{t('How to play')}</h2>
          <button
            type="button"
            className={styles.close}
            aria-label={t('Close')}
            onClick={close}
          >
            <img {...imgProps('Icon_Close')} alt="" />
          </button>
        </div>

        <div className={styles.body}>
          {layout ? (
            <RulesViewer layout={layout} />
          ) : (
            STEPS.map(([title, text], index) => (
              <div className={styles.step} key={title}>
                <span className={styles.stepNumber}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className={styles.rule}>
                  <span className={styles.ruleTitle}>{t(title)}</span>
                  <span className={styles.ruleText}>{t(text)}</span>
                </div>
              </div>
            ))
          )}
        </div>

        {seconds > 0 && (
          <p className={styles.active}>
            {t('Active round · auto-roll in {{seconds}}s', { seconds })}
          </p>
        )}
      </div>
    </div>
  );
};

export default GameRulesModal;
